import { yupResolver } from '@hookform/resolvers/yup';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Outlet } from 'react-router-dom';
import { Row, Col, Container, Alert, Button } from 'reactstrap';

import { useConfig } from '../../../_components/config';
import { DataLoader } from '../../../_components/data-loader';
import { useCheckSession } from '../../../_components/use-check-session';
import { useMocks } from '../mocks/use-mocks';

import type { WizardFormType } from './steps/use-steps';
import { useSteps, StepsProvider } from './steps/use-steps';
import { useSubmitSteps } from './steps/use-submit-steps';
import styles from './styles.module.scss';
import { Submission } from './submission';
import WizardStepper from './wizard';
import WizardNavigator from './wizard/navigator';
import StepHeader from './wizard/step-header';

export function Form() {
  return (
    <StepsProvider>
      <ProviderForm />
    </StepsProvider>
  );
}

function ProviderForm() {
  const [secondsUntilExpiration, setSecondsUntilExpiration] = useState<
    number | null
  >(null);
  const { steps } = useSteps();
  const mockData = useMocks();
  const { features } = useConfig();
  const { sessionId, session, extend, continued, isExpiring, expiresAt } =
    useCheckSession();
  const { submitted, request, onSubmitSteps, isSubmitting, setSubmitted } =
    useSubmitSteps(sessionId, session);

  const wizardFormSchema = useMemo(() => {
    const [head, ...rest] = steps.map((s) => s.schema);
    // eslint-disable-next-line unicorn/prefer-spread
    const newSchema = rest.reduce((acc, val) => acc.concat(val), head as any);
    return newSchema;
  }, [steps]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (isExpiring && !continued && expiresAt) {
        setSecondsUntilExpiration(differenceInSeconds(expiresAt, new Date()));
      }
    }, 1000);
    if (continued) {
      clearInterval(timer);
      // TODO CCRA-37242
      setSecondsUntilExpiration(null);
    }
    return () => {
      clearInterval(timer);
    };
  }, [setSecondsUntilExpiration, expiresAt, continued, isExpiring]);

  const form = useForm<WizardFormType>({
    mode: 'all',
    resolver: yupResolver(wizardFormSchema),
    context: { features },
    defaultValues: wizardFormSchema.getDefault(),
  });
  const {
    handleSubmit,
    // getValues
  } = form;
  return (
    <FormProvider {...form}>
      <DataLoader mockData={mockData} />
      {submitted ? (
        <Submission submissionResult={request} setSubmitted={setSubmitted} />
      ) : (
        <form
          onSubmit={handleSubmit((d) => onSubmitSteps(d))}
          spellCheck="false"
        >
          <fieldset disabled={isSubmitting}>
            <Container
              fluid
              className={`${styles.container} py-2`}
              style={{
                maxWidth: '1200px',
                minHeight: '500px',
              }}
            >
              <Row className="pt-4 mx-4 pb-4">
                <Col>
                  {isExpiring &&
                    !continued &&
                    secondsUntilExpiration !== null && (
                      <Alert color="warning" fade={false}>
                        This form will timeout in {secondsUntilExpiration}{' '}
                        seconds due to inactivity. Please click Continue to
                        extend your session.
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => {
                            extend();
                          }}
                          className="px-4 ms-3"
                        >
                          Continue
                        </Button>
                      </Alert>
                    )}
                  <WizardStepper />
                  <StepHeader />
                  <span role="main" id="main-content">
                    <Outlet />
                    <hr
                      aria-hidden="true"
                      style={{ backgroundColor: '#7f8083' }}
                    />
                    <WizardNavigator isSubmitting={isSubmitting} />
                  </span>
                </Col>
              </Row>
            </Container>
          </fieldset>
        </form>
      )}
    </FormProvider>
  );
}
